import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchViews,
  createView as createViewService,
  deleteView as deleteViewService,
  updateView as updateViewService,
} from '@services/index';
import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';
import { ICreateViewPayload, IView, IUpdateViewPayload } from '@insights_module/types/View';

export const useViews = () => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: [INSIGHTS_QUERY_KEY.views],
    queryFn: async () => await fetchViews(),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minute
  });

  const getDefaultView = (): IView | undefined => {
    return data?.data.find((view: IView) => view.default);
  };

  const getViewById = (id: string): IView | undefined => {
    return data?.data.find((view: IView) => view.id === id);
  };

  const getViewByName = (name: string): IView | undefined => {
    return data?.data.find((view: IView) => view.name === name);
  };

  const { mutateAsync: createView, isLoading: isCreateViewLoading } = useMutation({
    mutationFn: async (payload: ICreateViewPayload) => await createViewService(payload),
    onSuccess: () => {
      queryClient.invalidateQueries([INSIGHTS_QUERY_KEY.views]);
    },
  });

  const { mutateAsync: updateView, isLoading: isUpdateViewLoading } = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: IUpdateViewPayload }) =>
      await updateViewService(id, payload),
    onSuccess: () => queryClient.invalidateQueries([INSIGHTS_QUERY_KEY.views]),
  });

  const { mutateAsync: deleteView, isLoading: isDeleteViewLoading } = useMutation({
    mutationFn: async (id: string) => await deleteViewService(id),
    onSuccess: () => {
      queryClient.invalidateQueries([INSIGHTS_QUERY_KEY.views]);
    },
  });

  return {
    views: data?.data,
    getDefaultView,
    getViewById,
    getViewByName,
    createView,
    isCreateViewLoading,
    deleteView,
    isDeleteViewLoading,
    updateView,
    isUpdateViewLoading,
  };
};
